import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import { isEmpty, map, without } from 'lodash';
import cx from 'classnames';
import { isBlank, isPresent } from '@seedlang/utils';
import FilterComponent from 'components/backend_filter/filter_component';
import FilterButton from 'components/backend_filter/filter_button';
import FilterButtonText from 'components/backend_filter/filter_button_text';
import styled from '@emotion/styled';

const Wrapper = styled('div', {
  shouldForwardProp: prop => prop !== 'open',
})`
  position: relative;
  z-index: ${props => props.open ? 21 : 'auto'};
`;

const Trigger = styled.div`
  display: flex;
  align-items: center;
  i {
    margin-left: 6px;
  }
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 20;
  min-width: 240px;
  max-width: 320px;
  background: #FFF;
  border: 1px solid #A2A2A2;
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 6px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding-left: 8px !important;
  margin-bottom: 6px !important;
`;

const Options = styled.div`
  max-height: 220px;
  overflow-y: auto;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 6px;
  cursor: pointer;
  border-radius: 3px;
  font-size: 12px;
  &:hover {
    background: #f1f3f5;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
`;

const Empty = styled.div`
  padding: 8px 6px;
  color: #868e96;
  font-size: 12px;
`;

@observer
class FilterDropdownMultiSelect extends React.Component {
  @observable value = this.props.default;
  @observable open = false;
  @observable query = '';

  constructor(props) {
    super(props);
    this.wrapperRef = React.createRef();
  }

  componentDidMount() {
    let value = this.props.store.getFilterValue(this.props.name);
    if (!value || value === 'null') {
      value = [];
    }
    this.value = value;
    document.addEventListener('mousedown', this.onClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onClickOutside);
  }

  onClickOutside = (evt) => {
    if (this.wrapperRef.current && !this.wrapperRef.current.contains(evt.target)) {
      this.open = false;
      this.query = '';
    }
  }

  toggleOpen = () => {
    this.open = !this.open;
    if (!this.open) {
      this.query = '';
    }
  }

  clearIndexData = () => {
    this.updateFilter(null);
  }

  selectAll = () => {
    const ids = map(this.props.options, item => { return item.id; });
    this.updateFilter(ids);
  }

  toggleOption(option) {
    let options = [];
    if (isEmpty(this.value)) {
      options.push(option);
    } else if (!this.value?.includes(option)) {
      options = this.value;
      options.push(option);
    } else {
      options = without(this.value, option);
    }
    this.updateFilter(options);
  }

  updateFilter(value) {
    this.value = value;
    if (value) {
      this.props.store.setFilter(this.props.name, value, { refresh: true });
    } else {
      this.props.store.removeFilter(this.props.name, { persistRemoval: true, refresh: true });
    }
  }

  label(item) {
    if (this.props.labelField) {
      return item[this.props.labelField];
    }
    return item.name;
  }

  @computed get selected() {
    return isPresent(this.value);
  }

  @computed get triggerLabel() {
    const label = this.props.label || this.props.name;
    if (isBlank(this.value) || this.value.length === 0) {
      return `Select ${label}`;
    }
    return `${label} (${this.value.length})`;
  }

  @computed get filteredOptions() {
    const options = this.props.options || [];
    const query = this.query.trim().toLowerCase();
    if (isBlank(query)) {
      return options;
    }
    return options.filter(item => {
      return this.label(item)?.toLowerCase().includes(query);
    });
  }

  render() {
    return (
      <FilterComponent
        selected={this.selected}
      >
        <Wrapper
          ref={this.wrapperRef}
          open={this.open}
        >
          <FilterButton
            onClick={this.toggleOpen}
          >
            <Trigger>
              <FilterButtonText>
                {this.triggerLabel}
              </FilterButtonText>
              <i className={cx('fa', {
                'fa-caret-up': this.open,
                'fa-caret-down': !this.open,
              })} />
            </Trigger>
          </FilterButton>
          {
            this.open &&
              <Dropdown>
                <SearchInput
                  type='text'
                  value={this.query}
                  onChange={(evt) => this.query = evt.target.value}
                  placeholder={`Search ${this.props.label || this.props.name}`}
                />
                <Options>
                  {
                    this.filteredOptions.map(item => {
                      return (
                        <Option
                          key={item.id}
                          onClick={() => this.toggleOption(item.id)}
                        >
                          <i
                            className={cx('fa', {
                              'fa-check-circle': this.value?.includes(item.id),
                              'fa-ban': !this.value?.includes(item.id),
                            })}
                          />
                          <FilterButtonText>
                            {this.label(item)}
                          </FilterButtonText>
                        </Option>
                      );
                    })
                  }
                  {
                    isBlank(this.filteredOptions) &&
                      <Empty>
                        No matching {this.props.label || 'options'}
                      </Empty>
                  }
                </Options>
                <Actions>
                  <FilterButton
                    onClick={this.selectAll}
                  >
                    <i className='fa fa-check-circle' />
                  </FilterButton>
                  <FilterButton
                    onClick={this.clearIndexData}
                  >
                    <i className='fa fa-ban' />
                  </FilterButton>
                </Actions>
              </Dropdown>
          }
        </Wrapper>
      </FilterComponent>
    );
  }
}

export default FilterDropdownMultiSelect;
